/**
 * ColumnExpandModal — the Ctrl+Shift+E "expand a table's columns" dialog.
 *
 * Opened by an editor's onExpandColumns callback (see CodeMirrorSQL). Lets the
 * user pick a table from the current statement's FROM/JOIN list (or, via "show
 * all tables", any table of the current DB), then build a comma-separated column
 * list in one of two modes:
 *   • Except  — emit every column EXCEPT the ones picked (the classic behaviour).
 *   • Include — emit ONLY the picked columns (in table order).
 * Columns can be picked by clicking them in the color-coded list (PK / NOT NULL
 * are tinted distinctly) or by typing into the validated chip input. Columns are
 * prefixed with the table's alias ONLY when the statement already gave it one
 * (otherwise bare). The host passes connId + currentDb.
 *
 * Column names come from the shared completion cache (instant, offline), but on
 * table-select we ALSO fetch full column metadata (…/tables/{t}/columns) so PK +
 * NOT NULL can be tinted — the cache's completion shape carries only name+type.
 * The fetch is best-effort: on failure we fall back to the cache (no tints).
 *
 * Props:
 *   open       bool
 *   onClose    () => void
 *   connId     number | () => number
 *   currentDb  string | () => string
 *   request    { statementText, fromTables: [{db,table,alias}], selectedText, insert(text) }
 *              selectedText = the editor's highlighted text (if any) → auto-selects
 *              the matching table on open.
 */
import { createSignal, createMemo, createEffect, createResource, For, Show } from 'solid-js';
import Modal from './Modal.jsx';
import { completionState, tableColumns, findColumn } from '../lib/schema.js';
import { api } from '../lib/api.js';
import { toast } from '../lib/toast.js';

const val = (x) => (typeof x === 'function' ? x() : x);

// Reduce a highlighted editor selection to a bare table name, then find the
// matching table option. Handles `tuser`, `tuser a` (alias), `dbbayiku.tuser`
// (qualified), and backtick-quoting, case-insensitively.
function matchSelectedTable(opts, selectedText) {
  let s = (selectedText || '').trim();
  if (!s) return null;
  s = s.split(/\s+/)[0];                                  // "tuser a" → "tuser"
  if (s.includes('.')) s = s.slice(s.lastIndexOf('.') + 1); // "db.tuser" → "tuser"
  s = s.replace(/`/g, '').toLowerCase();
  if (!s) return null;
  return opts.find((o) => o.table.toLowerCase() === s) || null;
}

export default function ColumnExpandModal(props) {
  const connId = () => val(props.connId);
  const currentDb = () => val(props.currentDb) || '';
  const databases = () => (connId() ? completionState(connId())().databases : []) || [];

  const [showAll, setShowAll] = createSignal(false);
  const [selected, setSelected] = createSignal(null); // {db, table, alias}
  const [mode, setMode] = createSignal('except');      // 'except' | 'include'
  const [picks, setPicks] = createSignal([]);          // chosen column names (real-case)
  const [draft, setDraft] = createSignal('');
  const [err, setErr] = createSignal('');

  // The statement's FROM/JOIN tables, de-duplicated (db|table|alias).
  const stmtTables = createMemo(() => {
    const seen = new Set();
    const out = [];
    for (const t of (props.request?.fromTables || [])) {
      const key = `${t.db || ''}|${t.table}|${t.alias || ''}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({ db: t.db || null, table: t.table, alias: t.alias || '' });
    }
    return out;
  });

  // Every table of the current DB (adopting any alias the statement gave it, so
  // prefixing matches the editor). Used by "show all" + the selection matcher.
  const allDbTables = createMemo(() => {
    const db = databases().find((d) => d.database === currentDb());
    if (!db) return [];
    const aliasOf = (name) => stmtTables().find((t) => t.table === name)?.alias || '';
    return (db.tables || []).map((t) => ({ db: currentDb(), table: t.name, alias: aliasOf(t.name) }));
  });
  // The table options shown: the statement's tables, or every table of the
  // current DB when "show all" is ticked.
  const tableOptions = createMemo(() => (showAll() ? allDbTables() : stmtTables()));

  // Full column metadata (incl. isPrimary + nullable) for the selected table,
  // fetched on demand so the list can tint PK / NOT NULL. Best-effort: on failure
  // we return null and fall back to the completion cache (names only, no tints).
  const [fetchedCols] = createResource(
    () => {
      const s = selected();
      if (!props.open || !s || !connId()) return null;
      return { id: connId(), db: s.db || currentDb(), table: s.table };
    },
    async ({ id, db, table }) => {
      try {
        const r = await api.get(
          `/api/connections/${id}/databases/${encodeURIComponent(db)}/tables/${encodeURIComponent(table)}/columns`
        );
        return Array.isArray(r.columns) && r.columns.length ? r.columns : null;
      } catch {
        return null;
      }
    }
  );

  // Columns of the selected table — prefer the fetched (tintable) metadata, fall
  // back to the cache's name/type while it loads or if the fetch failed.
  const columns = createMemo(() => {
    const fetched = fetchedCols();
    if (fetched) return fetched;
    const s = selected();
    if (!s) return null;
    return tableColumns(databases(), s.db || currentDb(), s.table);
  });

  // Live preview of what Generate will insert. Except → all but the picks;
  // Include → only the picks. Both keep the table's column order (predictable,
  // and dedupes naturally).
  const preview = createMemo(() => {
    const s = selected();
    const cols = columns();
    if (!s || !cols) return '';
    const set = new Set(picks().map((c) => c.toLowerCase()));
    const pfx = s.alias ? s.alias + '.' : '';
    const keep = mode() === 'include'
      ? cols.filter((c) => set.has(String(c.name).toLowerCase()))
      : cols.filter((c) => !set.has(String(c.name).toLowerCase()));
    return keep.map((c) => pfx + c.name).join(', ');
  });

  // Tint class for a column in the list: PK wins over plain NOT NULL.
  const colClass = (c) => (c.isPrimary ? 'is-pk' : (c.nullable === false ? 'is-notnull' : ''));
  const isPicked = (name) => picks().some((p) => p.toLowerCase() === String(name).toLowerCase());

  const sameSel = (a, b) => a && b && (a.db || '') === (b.db || '') && a.table === b.table && (a.alias || '') === (b.alias || '');

  // Clear the inputs each time the dialog opens (the component stays mounted, so
  // signals persist between opens).
  let wasOpen = false;
  createEffect(() => {
    const open = props.open;
    if (open && !wasOpen) {
      setPicks([]); setDraft(''); setErr(''); setMode('except');
      // Auto-select the table the user highlighted in the editor. Prefer a table
      // already in the statement; otherwise fall back to any table in the current
      // DB (ticking "show all" so it's in the option list).
      const sel = props.request?.selectedText;
      const inStmt = matchSelectedTable(stmtTables(), sel);
      if (inStmt) { setShowAll(false); setSelected(inStmt); }
      else {
        const inDb = matchSelectedTable(allDbTables(), sel);
        if (inDb) { setShowAll(true); setSelected(inDb); }
      }
    }
    wasOpen = open;
  });

  // Keep a valid selection as the option list changes (statement change, schema
  // load, show-all toggle) — keep the current pick if still present, else first.
  createEffect(() => {
    const opts = tableOptions();
    setSelected((cur) => opts.find((o) => sameSel(o, cur)) || opts[0] || null);
  });

  // When the chosen TABLE actually changes, the picked chips (validated against
  // the old table's columns) are stale — drop them.
  let lastKey = null;
  createEffect(() => {
    const s = selected();
    const key = s ? `${s.db || ''}|${s.table}` : null;
    if (key !== lastKey) { lastKey = key; setPicks([]); setErr(''); }
  });

  const pick = (opt) => setSelected(opt);

  // Add a column name to the pick list (typed-in path; validated). Returns the
  // canonical name or null (unknown → sets err).
  const addPick = (raw) => {
    const name = (raw ?? '').trim();
    setDraft('');
    if (!name) return;
    const cols = columns();
    if (!cols) { setErr('Schema for this table isn’t loaded yet.'); return; }
    const col = findColumn(cols, name);
    if (!col) { setErr(`“${name}” is not a column of ${selected()?.table}.`); return; }
    if (!isPicked(col.name)) setPicks([...picks(), col.name]);
    setErr('');
  };
  const removePick = (i) => setPicks(picks().filter((_, idx) => idx !== i));
  // Click a column in the list → toggle it in/out of the pick list.
  const togglePick = (name) => {
    setErr('');
    if (isPicked(name)) setPicks(picks().filter((p) => p.toLowerCase() !== String(name).toLowerCase()));
    else setPicks([...picks(), name]);
  };
  const onDraftKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); addPick(draft()); }
    else if (e.key === 'Backspace' && draft() === '' && picks().length) removePick(picks().length - 1);
  };

  // When the expander was triggered from a highlighted selection, that text was
  // only used to pick the table — so DON'T overwrite it. Copy the generated list
  // to the clipboard instead. With no selection, insert at the cursor as usual.
  const triggeredFromSelection = () => !!(props.request?.selectedText || '').trim();
  const generate = async () => {
    const out = preview();
    if (!out) { setErr('No columns left to insert.'); return; }
    if (triggeredFromSelection()) {
      try {
        await navigator.clipboard.writeText(out);
        toast.success('Columns copied to clipboard');
      } catch {
        toast.error('Could not copy to clipboard');
      }
    } else {
      props.request?.insert?.(out);
    }
    props.onClose?.();
  };

  const tableLabel = (o) => (o.alias ? `${o.table} ${o.alias}` : o.table);
  const optKey = (o) => `${o.db || ''}|${o.table}|${o.alias || ''}`;

  return (
    <Modal open={props.open} onClose={props.onClose} title="Expand columns" wide>
      <div class="col-expand">
        <div class="col-expand-row">
          <label class="col-expand-label">Table</label>
          <Show when={tableOptions().length > 0}
                fallback={<div class="muted" style="font-size:12px;">No tables in the current statement — tick “Show all tables”.</div>}>
            <select class="input"
                    value={selected() ? optKey(selected()) : ''}
                    onChange={(e) => pick(tableOptions().find((o) => optKey(o) === e.target.value) || null)}>
              <For each={tableOptions()}>
                {(o) => <option value={optKey(o)}>{tableLabel(o)}</option>}
              </For>
            </select>
          </Show>
        </div>

        <label class="col-expand-check">
          <input type="checkbox" checked={showAll()} onChange={(e) => setShowAll(e.target.checked)} />
          Show all tables in {currentDb() || 'current DB'}
        </label>

        <div class="col-expand-row">
          <label class="col-expand-label">Mode</label>
          <div class="col-expand-modes" role="group" aria-label="Column selection mode">
            <button type="button" class="col-expand-mode"
                    classList={{ active: mode() === 'except' }}
                    onClick={() => setMode('except')}
                    title="Emit every column except the ones picked">Except</button>
            <button type="button" class="col-expand-mode"
                    classList={{ active: mode() === 'include' }}
                    onClick={() => setMode('include')}
                    title="Emit only the columns picked">Include</button>
          </div>
        </div>

        <div class="col-expand-row">
          <label class="col-expand-label">{mode() === 'include' ? 'Include' : 'Except'}</label>
          <div class="tags-input" classList={{ 'has-error': !!err() }}>
            <For each={picks()}>
              {(c, i) => (
                <span class="tag-chip">{c}
                  <button type="button" class="tag-chip-x" onClick={() => removePick(i())}>×</button>
                </span>
              )}
            </For>
            <input class="tags-input-field"
                   value={draft()}
                   placeholder={picks().length ? '' : (mode() === 'include' ? 'Click a column below, or type + Enter…' : 'Column to exclude, Enter…')}
                   onInput={(e) => setDraft(e.target.value)}
                   onKeyDown={onDraftKey}
                   onBlur={() => addPick(draft())} />
          </div>
        </div>

        <Show when={err()}><div class="col-expand-err">{err()}</div></Show>

        {/* Clickable column list — click to toggle a column in/out of the picks.
            PK + NOT NULL columns are tinted (legend below). */}
        <Show when={columns()}>
          <div class="col-expand-row">
            <label class="col-expand-label">
              Columns <span class="col-expand-legend">
                <span class="col-expand-legend-item is-pk">PK</span>
                <span class="col-expand-legend-item is-notnull">NOT NULL</span>
              </span>
            </label>
            <div class="col-expand-cols">
              <For each={columns()}>
                {(c) => (
                  <button type="button"
                          class={`col-expand-col ${colClass(c)}`}
                          classList={{ picked: isPicked(c.name) }}
                          title={`${c.name}${c.type ? ' · ' + c.type : ''}${c.isPrimary ? ' · PK' : (c.nullable === false ? ' · NOT NULL' : '')} — click to ${isPicked(c.name) ? 'remove' : 'add'}`}
                          onClick={() => togglePick(c.name)}>
                    {c.name}
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>

        <div class="col-expand-preview">
          <label class="col-expand-label">Preview</label>
          <code class="col-expand-code">{preview() || '—'}</code>
        </div>

        <div class="col-expand-actions">
          <button class="btn btn-ghost" onClick={props.onClose}>Cancel</button>
          <button class="btn btn-primary" disabled={!preview()} onClick={generate}>
            {triggeredFromSelection() ? 'Copy columns' : 'Insert'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
